/** @jsxRuntime automatic */
/** @jsxImportSource react */
import { CONVERSATION, REPORT_MARKDOWN, ROSTER } from '../../lib/scenario.ts'
import { downloadText } from '../lib/download.ts'

/**
 * Honest previews for the three modules whose live embed isn't wired yet
 * (LANDING_PAGE.md F2 — monkeytalk's browser surface and monkeycard's Rust
 * render path are unconfirmed; monkeydoc isn't on npm and needs vendoring).
 *
 * These render REAL scenario data and hand over REAL files, so they're useful
 * and truthful — but they are not the shipping components, and the UI says so
 * in DemoDeck's badge. Replace each one as its embed path lands.
 */

function PreviewNote({ children }: { children: React.ReactNode }) {
  return (
    <p className='mt-4 rounded-lg border border-dashed border-[var(--edge)] bg-[var(--surface-sunken)] p-3 text-xs text-[var(--ink-muted)]'>
      {children}
    </p>
  )
}

// ── monkeydoc ───────────────────────────────────────────────────────────

export function DocPreview() {
  const words = REPORT_MARKDOWN.trim().split(/\s+/).length
  const headings = REPORT_MARKDOWN.match(/^#{1,6} /gm)?.length ?? 0
  const links = REPORT_MARKDOWN.match(/\[[^\]]+\]\([^)]+\)/g)?.length ?? 0

  return (
    <div>
      <div className='grid gap-4 lg:grid-cols-2'>
        <div>
          <p className='mb-2 text-xs font-semibold text-[var(--ink-muted)]'>What you'd see</p>
          <div className='scroll-x max-h-80 overflow-y-auto rounded-xl border border-[var(--edge)] bg-[var(--surface)] p-4'>
            <pre className='font-mono text-xs leading-relaxed whitespace-pre-wrap'>
              {REPORT_MARKDOWN}
            </pre>
          </div>
        </div>
        <div>
          {
            /* Was an internal AST dump. Removed 2026-08: the document's on-disk
              representation is an implementation detail, and publishing a
              schema name invites people to couple to it. What a visitor can
              actually use is the CLI — so show that instead. */
          }
          <p className='mb-2 text-xs font-semibold text-[var(--ink-muted)]'>
            What an agent can do with it
          </p>
          <div className='max-h-80 overflow-y-auto rounded-xl border border-[var(--edge)] bg-[var(--surface)] p-4'>
            <pre className='font-mono text-xs leading-relaxed'>
              <span className='text-[var(--ink-muted)]'>$</span>{' '}
              monkeydoc stats q3-report.md{'\n'}
              {JSON.stringify(
                { word_count: words, heading_count: headings, link_count: links },
                null,
                2,
              )}
              {'\n\n'}
              <span className='text-[var(--ink-muted)]'>$</span>{' '}
              monkeydoc sections q3-report.md --paths{'\n'}
              {'Q3 Report\nQ3 Report › Summary\nQ3 Report › By region\nQ3 Report › Risks'}
              {'\n\n'}
              <span className='text-[var(--ink-muted)]'>$</span>{' '}
              monkeydoc export q3-report.md -o q3.docx
            </pre>
          </div>
          <p className='mt-2 text-xs text-[var(--ink-muted)]'>
            Real commands, real output. Your agent drives the document the same way you do.
          </p>
        </div>
      </div>

      <div className='mt-4 flex flex-wrap gap-3'>
        <button
          type='button'
          onClick={() => downloadText('q3-report.md', REPORT_MARKDOWN, 'text/markdown')}
          className='rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--brand-ink)] hover:brightness-105'
        >
          ↓ Download q3-report.md
        </button>
        <p className='self-center text-sm text-[var(--ink-muted)]'>
          Plain markdown — no lock-in to prove.
        </p>
      </div>

      <PreviewNote>
        The shipping demo puts the real <code className='font-mono'>&lt;MonkeyDoc&gt;</code>{' '}
        editor here, editable in place. <strong>Not wired yet:</strong>{' '}
        monkeydoc isn't published to npm, so it needs the vendored-dist path before it can run here.
      </PreviewNote>
    </div>
  )
}

// ── monkeytalk ──────────────────────────────────────────────────────────

export function ChatPreview() {
  const transcript = CONVERSATION
    .map((m) => `${m.at} ${m.from}: ${m.body}\n     ${m.ev}`)
    .join('\n')

  return (
    <div>
      <div className='grid gap-4 lg:grid-cols-[220px_1fr]'>
        <div className='rounded-xl border border-[var(--edge)] bg-[var(--surface)] p-4'>
          <p className='mb-3 text-xs font-semibold text-[var(--ink-muted)]'>Roster</p>
          <ul className='space-y-2'>
            {ROSTER.map((p) => (
              <li key={p.id} className='flex items-center gap-2'>
                <span className='grid size-7 shrink-0 place-items-center rounded-full bg-[var(--surface-sunken)] text-xs'>
                  {p.kind === 'agent' ? '🤖' : '🧑'}
                </span>
                <span className='min-w-0'>
                  <span className='block truncate text-sm font-medium'>{p.name}</span>
                  <span className='block font-mono text-[10px] text-[var(--ink-muted)]'>
                    {p.kind}
                  </span>
                </span>
              </li>
            ))}
          </ul>
          <p className='mt-3 border-t border-[var(--edge)] pt-3 text-xs text-[var(--ink-muted)]'>
            Two of these are agents. Same list, same verbs.
          </p>
        </div>

        <div className='max-h-80 overflow-y-auto rounded-xl border border-[var(--edge)] bg-[var(--surface)] p-4'>
          <ul className='space-y-3'>
            {CONVERSATION.map((m) => {
              const who = ROSTER.find((p) => p.id === m.from)
              return (
                <li key={m.ev}>
                  <p className='flex items-baseline gap-2'>
                    <span className='text-sm font-semibold'>{who?.name ?? m.from}</span>
                    {who?.kind === 'agent' && (
                      <span className='rounded bg-[var(--accent)]/15 px-1 text-[10px] font-medium text-[var(--accent)]'>
                        agent
                      </span>
                    )}
                    <span className='font-mono text-[10px] text-[var(--ink-muted)]'>{m.at}</span>
                  </p>
                  <p className='mt-0.5 text-sm text-[var(--ink-muted)]'>{m.body}</p>
                  <p className='font-mono text-[10px] text-[var(--ink-muted)] opacity-60'>
                    {m.ev}
                  </p>
                </li>
              )
            })}
          </ul>
        </div>
      </div>

      <div className='mt-4 flex flex-wrap gap-3'>
        <button
          type='button'
          onClick={() => downloadText('kettle-and-rye.transcript.md', transcript, 'text/markdown')}
          className='rounded-full bg-[var(--brand)] px-4 py-2 text-sm font-semibold text-[var(--brand-ink)] hover:brightness-105'
        >
          ↓ Download the transcript
        </button>
        <p className='self-center text-sm text-[var(--ink-muted)]'>
          The real demo hands you the <code className='font-mono'>.mtalk</code> itself.
        </p>
      </div>

      <PreviewNote>
        The shipping demo is the live chat component, and the download is the actual{' '}
        <code className='font-mono'>.mtalk</code>{' '}
        file — your whole conversation, one file, openable with the CLI.{' '}
        <strong>Not wired yet:</strong>{' '}
        monkeytalk's browser-embeddable surface needs confirming (it has a{' '}
        <code className='font-mono'>component</code> package — that's the spike).
      </PreviewNote>
    </div>
  )
}

// ── monkeycard ──────────────────────────────────────────────────────────

const CARDS = [
  { title: 'Q3 — Kettle & Rye', kind: 'title' },
  { title: 'Revenue up 12%', kind: 'stat' },
  { title: 'North leads at +18%', kind: 'stat' },
  { title: 'Two regions at risk', kind: 'list' },
  { title: 'Coast is shrinking', kind: 'list' },
  { title: 'Q4 asks', kind: 'title' },
]

const THEMES = ['Kraft', 'Midnight', 'Paper'] as const

export function DeckPreview() {
  return (
    <div>
      <div className='mb-4 flex flex-wrap items-center gap-2'>
        <span className='text-xs font-semibold text-[var(--ink-muted)]'>Theme</span>
        {THEMES.map((t, i) => (
          <span
            key={t}
            className={`rounded-full border px-3 py-1 text-xs ${
              i === 0
                ? 'border-[var(--brand)] bg-[var(--brand)]/15 font-semibold'
                : 'border-[var(--edge)] text-[var(--ink-muted)]'
            }`}
          >
            {t}
          </span>
        ))}
        <span className='text-xs text-[var(--ink-muted)]'>
          — swapping these never touches a word of the markdown
        </span>
      </div>

      <div className='scroll-x'>
        <ul className='flex min-w-max gap-3'>
          {CARDS.map((c, i) => (
            <li
              key={c.title}
              className='grid aspect-video w-52 shrink-0 place-items-center rounded-lg border border-[var(--edge)] bg-[var(--surface)] p-4 text-center'
            >
              <div>
                <p className='font-mono text-[10px] text-[var(--ink-muted)]'>
                  {String(i + 1).padStart(2, '0')}
                </p>
                <p className='mt-1 text-sm font-bold'>{c.title}</p>
              </div>
            </li>
          ))}
        </ul>
      </div>

      <PreviewNote>
        The shipping demo re-renders this deck live as you switch themes, with the markdown beside
        it visibly <em>not</em> changing — and hands you a real{' '}
        <code className='font-mono'>.pptx</code>. <strong>Not wired yet:</strong>{' '}
        monkeycard renders through a Rust engine, so it needs either a wasm build or a server-render
        endpoint. This is the biggest open technical question on the page.
      </PreviewNote>
    </div>
  )
}
